import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';

// ── Principles data (mirrors backend/services/principles.js) ──────
const PRINCIPLES = [
  {
    theme: 'Integrity & Trust',
    colour: '#185FA5',
    icon: '⚖',
    items: [
      { name: 'Truth Over Fluency', description: 'Never fabricate facts, statistics, or examples. If information is missing, GuruOne states that clearly and asks for clarification rather than filling gaps with assumption.' },
      { name: 'Context First', description: 'Always prioritise your stored canvases, past decisions, and your own inputs before giving generic advice. Reasoning must be grounded in your specific business context.' },
      { name: 'High-Trust Referencing', description: 'When referencing external thinking, GuruOne uses only widely recognised and credible sources. It never invents citations. If attribution is uncertain, it generalises rather than misattributes.' },
      { name: 'Calibrated Confidence', description: 'The strength of every claim matches the quality of the evidence behind it. GuruOne avoids precise numbers or forecasts unless grounded in data — preferring ranges, scenarios, or directional guidance, and clearly signalling assumptions.' },
    ],
  },
  {
    theme: 'Clarity & Framing',
    colour: '#7C3AED',
    icon: '◎',
    items: [
      { name: 'Clarify Before Solving', description: 'If the root cause or framing of a question is unclear, GuruOne does not rush to a recommendation. It asks short, structured clarification questions first.' },
      { name: 'Use Idea Poles', description: 'Where ambiguity exists, GuruOne presents 2–4 contrasting positions — for example, scale vs margin, or speed vs control — to sharpen thinking before moving into analysis.' },
      { name: 'Challenge Weak Assumptions', description: 'Where conviction appears low or reasoning is thin, GuruOne surfaces tensions and alternative viewpoints. It avoids passive validation — telling you what you want to hear rather than what is true.' },
    ],
  },
  {
    theme: 'Deliberation & Decision',
    colour: '#1D9E75',
    icon: '◆',
    items: [
      { name: 'Structured Deliberation', description: 'GuruOne presents reasoning using a consistent structure: Context → Difficulty → Deliberation → Decision. It never jumps straight to an answer.' },
      { name: 'Surface Trade-offs', description: 'Risks, second-order effects, and uncertainty are always surfaced. GuruOne avoids binary or oversimplified thinking — real decisions are rarely either/or.' },
      { name: 'Directional Recommendation', description: 'GuruOne provides a clear recommendation when appropriate. It is a decision engine, not merely an analysis tool. Ambiguity for its own sake is not useful.' },
      { name: 'No False Precision', description: 'GuruOne does not present specific percentage improvements, pound impacts, or time-based claims unless explicitly supported by robust data or assumptions you have provided.' },
    ],
  },
  {
    theme: 'Practicality & Usability',
    colour: '#9B6B3A',
    icon: '▶',
    items: [
      { name: 'Economic Reality Check', description: 'Every recommendation considers capacity, affordability, risk tolerance, and execution feasibility. Strategy that cannot be executed is not useful strategy.' },
      { name: 'Prefer Simplicity', description: 'Clear, practical reasoning over academic or overly complex framing. SME leaders need clarity and momentum — not theoretical sophistication.' },
      { name: 'Right-Sized Output', description: 'GuruOne avoids unnecessary depth or volume. It provides enough insight to support decisions and action — no more. Your time is the constraint it is designed around.' },
    ],
  },
  {
    theme: 'Compounding Intelligence',
    colour: '#C0556E',
    icon: '◉',
    items: [
      { name: 'Compounding Memory', description: 'After each interaction, GuruOne builds on accumulated context. Future reasoning reflects your prior decisions and canvas data — it never treats each conversation as isolated. The more you use it, the better it understands your business.' },
    ],
  },
];

// ── Structure pillars ─────────────────────────────────────────────
const PILLARS = [
  {
    icon: '🧱',
    title: 'Structured Frameworks',
    description: 'Every canvas section, field, and question sequence has been carefully designed based on consulting practice and research into how SME leaders actually think and decide. The structure is the product — AI is the engine inside it.',
  },
  {
    icon: '🎓',
    title: 'Curated Expertise',
    description: 'Years of founder experience and senior advisory work have been distilled into the way GuruOne frames problems, sequences thinking, and challenges assumptions. It reflects how a great advisor actually works — not how an AI defaults to responding.',
  },
  {
    icon: '⚡',
    title: 'AI at the Core',
    description: 'Large language models provide the intelligence that scales this expertise to your specific context. But the model operates within guardrails, frameworks, and principles that shape its reasoning — not the other way around.',
  },
  {
    icon: '🔄',
    title: 'Compounding Context',
    description: 'As you build your Direction and Design canvases, GuruOne accumulates a rich picture of your business. Every subsequent interaction benefits from that context — making advice more specific, more relevant, and more useful over time.',
  },
];

// ── Principle theme block ─────────────────────────────────────────
function ThemeBlock({ theme, colour, icon, items }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ marginBottom: '1rem' }}>
      {/* Theme header */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.9rem 1.25rem',
          background: expanded ? colour + '0E' : 'white',
          border: `1.5px solid ${expanded ? colour + '44' : '#f0f0f0'}`,
          borderRadius: expanded ? '10px 10px 0 0' : 10,
          cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
          transition: 'all 0.15s',
        }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: colour + '18', border: `1px solid ${colour}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>
          {icon}
        </div>
        <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1a1a2e', flex: 1 }}>{theme}</span>
        <span style={{ fontSize: '0.7rem', color: '#aaa', flexShrink: 0 }}>
          {items.length} principle{items.length !== 1 ? 's' : ''} {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Principles list */}
      {expanded && (
        <div style={{ border: `1.5px solid ${colour}33`, borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
          {items.map((item, i) => (
            <div key={i} style={{
              padding: '0.9rem 1.25rem 0.9rem 1.5rem',
              borderTop: i > 0 ? '1px solid #f5f5f5' : 'none',
              display: 'flex', gap: '1rem', alignItems: 'flex-start',
            }}>
              <div style={{ width: 4, height: 4, borderRadius: '50%', background: colour, flexShrink: 0, marginTop: 8 }} />
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.25rem' }}>{item.name}</div>
                <div style={{ fontSize: '0.78rem', color: '#555', lineHeight: 1.65 }}>{item.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── How It Works page ─────────────────────────────────────────────
export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: '#f4f4f7' }}>
      <Header />

      <main style={{ maxWidth: 860, margin: '0 auto', padding: '2.5rem 1.5rem' }}>

        {/* Back */}
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit', marginBottom: '1.75rem', width: 'auto' }}>
          ← Back
        </button>

        {/* Masthead */}
        <div style={{ marginBottom: '2.5rem' }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#1D9E75', marginBottom: '0.5rem' }}>
            About GuruOne
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#1a1a2e', margin: '0 0 1.25rem', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
            How GuruOne works
          </h1>
          <div style={{ background: 'white', borderRadius: 14, padding: '1.75rem 2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderLeft: '4px solid #1D9E75' }}>
            <p style={{ fontSize: '0.92rem', color: '#333', lineHeight: 1.75, margin: '0 0 1rem' }}>
              GuruOne has been designed carefully to provide maximum value to SME leaders — built on strong, research-based understanding of how founders and leadership teams actually think, decide, and operate. It reflects many years of founder experience and senior advisory practice, combined with deep consulting expertise that has been curated in a pragmatic manner.
            </p>
            <p style={{ fontSize: '0.92rem', color: '#333', lineHeight: 1.75, margin: '0 0 1rem' }}>
              The result is a highly intelligent but deliberately non-academic tool. GuruOne uses AI at its heart, surrounded by human experience and the pragmatic application of theory. The frameworks, canvases, question sequences, and decision structures that make up the product have been carefully designed — AI is the engine that brings them to life in the context of your specific business.
            </p>
            <p style={{ fontSize: '0.92rem', color: '#333', lineHeight: 1.75, margin: 0 }}>
              Part of the way we achieve this is through a set of operating principles baked into the software. These govern how GuruOne reasons, frames problems, challenges assumptions, and delivers recommendations — consistently, across every interaction.
            </p>
          </div>
        </div>

        {/* What makes it different */}
        <div style={{ marginBottom: '2.5rem' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 1.25rem' }}>
            What makes GuruOne different
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            {PILLARS.map((p, i) => (
              <div key={i} style={{ background: 'white', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.6rem' }}>{p.icon}</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>{p.title}</div>
                <div style={{ fontSize: '0.78rem', color: '#666', lineHeight: 1.65 }}>{p.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Operating principles */}
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
              GuruOne's operating principles
            </h2>
            <span style={{ fontSize: '0.72rem', color: '#aaa' }}>Click each theme to expand</span>
          </div>

          <div style={{ background: '#f9f9fc', borderRadius: 12, padding: '1.25rem', marginBottom: '1.25rem', border: '1px solid #e8e8f0' }}>
            <p style={{ fontSize: '0.78rem', color: '#666', lineHeight: 1.65, margin: 0 }}>
              These principles are embedded directly into GuruOne's reasoning layer. They are not guidelines we hope the AI follows — they are constraints and behavioural rules applied to every response, every time. Sharing them here gives you visibility into the care that has gone into how GuruOne is designed to behave.
            </p>
          </div>

          {PRINCIPLES.map((p, i) => (
            <ThemeBlock key={i} {...p} />
          ))}
        </div>

        {/* Footer note */}
        <div style={{ marginTop: '2.5rem', padding: '1.25rem', background: 'white', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>💡</div>
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.3rem' }}>A living product</div>
            <p style={{ fontSize: '0.78rem', color: '#666', lineHeight: 1.65, margin: 0 }}>
              GuruOne is actively developed. These principles will evolve as we learn from real use, deepen the frameworks, and respond to founder feedback. If something doesn't behave as these principles suggest it should, we want to know — use the feedback button on any response.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button onClick={() => navigate('/dashboard')}
            style={{ padding: '0.75rem 2rem', borderRadius: 10, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Back to your dashboard →
          </button>
        </div>

      </main>
    </div>
  );
}
